import { max, min, scaleLinear } from 'd3';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { LineChartAxis, LineChartDatum, LineChartSeries } from './ResponsiveLineChart.types';

export const axisWidth = 76;
const yDomainPaddingRatio = 0.05;
const yAxisTickCount = 4;

export function tooltipGroups(
  activeLabel: string,
  values: Array<{ item: LineChartSeries; datum: LineChartDatum }>,
) {
  const groups = new Map<string, Array<{ item: LineChartSeries; datum: LineChartDatum }>>();
  values.forEach((value) => {
    const label = value.datum.tooltipLabel ?? activeLabel;
    const group = groups.get(label) ?? [];
    group.push(value);
    groups.set(label, group);
  });
  return [...groups].map(([label, groupValues]) => ({ label, values: groupValues }));
}

export function legendId(series: LineChartSeries) {
  return series.legendGroupId ?? series.id;
}

export function chartLegendItems(series: LineChartSeries[]) {
  const items = new Map<string, LineChartSeries>();
  series.forEach((item) => {
    const id = legendId(item);
    if (!items.has(id)) items.set(id, item);
  });
  return [...items.values()];
}

export function activePoint(
  event: ReactPointerEvent<SVGElement>,
  container: HTMLDivElement | null,
  datum: LineChartDatum,
) {
  const bounds = container?.getBoundingClientRect();
  return {
    x: datum.x,
    label: datum.label,
    ...(bounds
      ? { left: event.clientX - bounds.left + 12, top: event.clientY - bounds.top + 12 }
      : {}),
  };
}

export function nearestDatumForPointer(
  event: ReactPointerEvent<SVGRectElement>,
  data: LineChartDatum[],
  xScale: (value: number) => number,
) {
  const bounds = event.currentTarget.getBoundingClientRect();
  const pointerX = event.clientX - bounds.left;
  return data.reduce<LineChartDatum | null>((closest, datum) => {
    if (!closest) return datum;
    const currentDistance = Math.abs(xScale(datum.x) - pointerX);
    const closestDistance = Math.abs(xScale(closest.x) - pointerX);
    return currentDistance < closestDistance ? datum : closest;
  }, null);
}

export function buildYScale(domain: [number, number], innerHeight: number) {
  return scaleLinear(paddedYDomain(domain), [innerHeight, 0]).nice(yAxisTickCount);
}

export function paddedYDomain([first, last]: [number, number]): [number, number] {
  if (first === last) {
    if (first === 0) return [0, 1];
    const padding = Math.abs(first) * yDomainPaddingRatio;
    return [first - padding, last + padding];
  }

  const padding = (last - first) * yDomainPaddingRatio;
  return [
    first >= 0 ? Math.max(0, first - padding) : first - padding,
    last <= 0 ? Math.min(0, last + padding) : last + padding,
  ];
}

export function formatterForSeries(
  series: LineChartSeries,
  axes: LineChartAxis[],
  fallback: (value: number) => string,
) {
  return axes.find((axis) => axis.id === series.axisId)?.valueFormatter ?? fallback;
}

export function extent(values: number[], fallback: [number, number]): [number, number] {
  const first = min(values);
  const last = max(values);
  if (first === undefined || last === undefined) return fallback;
  return first === last ? [first - 0.5, first + 0.5] : [first, last];
}

export function valueExtent(values: number[], fallback: [number, number]): [number, number] {
  const first = min(values);
  const last = max(values);
  return first === undefined || last === undefined ? fallback : [first, last];
}

export function chartXTicks(data: LineChartDatum[], maxTicks: number) {
  const positions = [...new Set(data.map((datum) => datum.x))].sort((left, right) => left - right);
  if (positions.length <= maxTicks) return positions;

  const lastIndex = positions.length - 1;
  const sampledIndexes = new Set(
    Array.from({ length: maxTicks }, (_, index) =>
      Math.round((index * lastIndex) / (maxTicks - 1)),
    ),
  );
  return [...sampledIndexes].flatMap((index) => {
    const position = positions[index];
    return position === undefined ? [] : [position];
  });
}

const compactNumberFormat = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function compactNumber(value: number) {
  return compactNumberFormat.format(value);
}
